import React, { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { useHistory } from "react-router-dom";
import WhatsAppService from "../../../../../service/WhatsAppService";
import GoogleCalendarService from "../../../../../service/GoogleCalendarService";
import WhatsappImg from "../../../../images/WhatsApp.svg";
import GoogleCalendarImg from "../../../../images/GoogleCalendar.svg";
import IntegrationSidebar from "./IntegrationSideBar/IntegrationSidebar";
import localeText from "../../../../../locale";
import { InputSwitch } from "primereact/inputswitch";
import ConfirmationModal from "./ConfirmationModal/ConfirmationModal";
import { TYPES } from "./ConfirmationModal/ModalTypes";

const StoreSettingsIntegrationsTab = ({
  customerIntegrationsPage = false,
  userInfo,
  allowedIntegrations = ["whatsapp", "google-calendar"],
}) => {
  const history = useHistory();
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [displayDialog, setDisplayDialog] = useState(false);
  const [configs, setConfigs] = useState({});
  const [whatsappStep, setWhatsappStep] = useState(1); // 1: phone, 2: code confirmation
  const [whatsappId, setWhatsappId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [whatsappConnected, setWhatsappConnected] = useState();
  const [googleCalendarConnected, setGoogleCalendarConnected] = useState();
  const [checkingConnection, setCheckingConnection] = useState(true);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [integrationToDelete, setIntegrationToDelete] = useState(null);
  const toast = useRef(null);
  const whatsAppService = useRef(new WhatsAppService()).current;
  const googleCalendarService = useRef(new GoogleCalendarService()).current;

  const checkWhatsAppConnection = useCallback(async () => {
    try {
      setCheckingConnection(true);
      const token = localStorage.getItem("__extraconomy_token");
      const response = await whatsAppService.getConnection(token);

      if (response.messageCode === 200001) {
        setWhatsappConnected(response.result);
      }
    } catch (error) {
      console.error("Erro ao verificar conexão WhatsApp:", error);
    } finally {
      setCheckingConnection(false);
    }
  }, [whatsAppService]);

  const checkGoogleCalendarConnection = useCallback(async () => {
    try {
      const token = localStorage.getItem("__extraconomy_token");
      const userId = userInfo?.id;

      if (!userId) {
        console.warn("userId não encontrado no userInfo");
        return;
      }

      const response = await googleCalendarService.getStatus(token, userId);

      if (response) {
        setGoogleCalendarConnected({
          connected: response?.connected ? response?.connected : false,
        });
      }
    } catch (error) {
      console.error("Erro ao verificar conexão Google Calendar:", error);
    }
  }, [googleCalendarService, userInfo]);

  useEffect(() => {
    checkWhatsAppConnection();
    checkGoogleCalendarConnection();
  }, [checkWhatsAppConnection, checkGoogleCalendarConnection]);

  const integrations = [
    {
      id: "whatsapp",
      name: "WhatsApp",
      url: WhatsappImg,
      description: localeText("whatsappIntegrationDescription"),
      icon: "pi-whatsapp",
      color: "#25d366",
      fields: [{ key: "phoneNumber", label: localeText("phoneNumber"), type: "text" }],
      isWhatsApp: true,
    },
    {
      id: "google-calendar",
      name: localeText("googleCalendar"),
      url: GoogleCalendarImg,
      description: localeText("googleCalendarDescription"),
      icon: "pi-calendar",
      color: "#4285F4",
      fields: [
        { key: "clientId", label: localeText("clientId"), type: "text" },
        { key: "clientSecret", label: localeText("clientSecret"), type: "text" },
        { key: "apiKey", label: localeText("apiKey"), type: "text" },
      ],
      isWhatsApp: false,
    },
  ].filter((integration) => allowedIntegrations.includes(integration.id));

  const handleConfigure = async (integration) => {
    if (integration.id === "google-calendar") {
      if (googleCalendarConnected?.connected) {
        history.push("/dashboard-store/schedules");
      } else {
        await handleGoogleCalendarAuth();
      }
      return;
    }

    setSelectedIntegration(integration);
    setWhatsappStep(1);
    setWhatsappId(null);
    setDisplayDialog(true);
  };

  const handleGoogleCalendarAuth = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("__extraconomy_token");
      const response = await googleCalendarService.getAuthUrl(token);

      if (response.messageCode === 200001 && response.result?.authorizationUrl) {
        window.open(response.result.authorizationUrl, "_blank");
        toast.current?.show({
          severity: "info",
          summary: localeText("success"),
          detail: localeText("redirectedToGoogleAuth"),
          life: 5000,
        });

        setTimeout(() => {
          checkGoogleCalendarConnection();
        }, 5000);
      } else {
        toast.current?.show({
          severity: "error",
          summary: localeText("error"),
          detail: response.message || localeText("errorGettingAuthUrl"),
          life: 5000,
        });
      }
    } catch (error) {
      console.error("Erro ao obter URL de autorização do Google Calendar:", error);
      toast.current?.show({
        severity: "error",
        summary: localeText("error"),
        detail: error.response?.data?.message || localeText("errorConnectingToServer"),
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsAppAuth = async () => {
    const phoneNumber = configs[`${selectedIntegration.id}_phoneNumber`];

    if (!phoneNumber || phoneNumber.trim() === "") {
      toast.current?.show({
        severity: "warn",
        summary: localeText("attention"),
        detail: localeText("pleaseProvidePhoneNumber"),
        life: 3000,
      });
      return;
    }

    try {
      setLoading(true);

      const token = localStorage.getItem("__extraconomy_token");

      if (whatsappConnected?.whatsappNumber && !whatsappConnected?.connected) {
        await whatsAppService.deleteConnection(token, whatsappConnected.id);
      }

      const response = await whatsAppService.authenticateWhatsApp(token, phoneNumber);

      if (response.messageCode === 200000) {
        setWhatsappId(response.result?.id || response.result);
        setWhatsappStep(2);
        toast.current?.show({
          severity: "success",
          summary: localeText("codeSent"),
          detail: localeText("confirmationCodeSentToWhatsApp"),
          life: 5000,
        });
      } else {
        toast.current?.show({
          severity: "error",
          summary: localeText("error"),
          detail: localeText("data").find((msg) => msg.code === response.messageCode).message,
          life: 5000,
        });
      }
    } catch (error) {
      console.error("Erro ao autenticar WhatsApp:", error);
      toast.current?.show({
        severity: "error",
        summary: localeText("error"),
        detail: error.response?.data?.message || localeText("errorConnectingToServer"),
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsAppConfirm = async () => {
    const confirmationCode = configs[`${selectedIntegration.id}_confirmationCode`];

    if (!confirmationCode || confirmationCode.trim() === "") {
      toast.current?.show({
        severity: "warn",
        summary: localeText("attention"),
        detail: localeText("pleaseProvideConfirmationCode"),
        life: 3000,
      });
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("__extraconomy_token");
      const response = await whatsAppService.confirmWhatsApp(token, confirmationCode, whatsappId);

      if (response.messageCode === 200000 || response.messageCode === 200001) {
        toast.current?.show({
          severity: "success",
          summary: localeText("success"),
          detail: localeText("whatsappConfiguredSuccessfully"),
          life: 3000,
        });
        setDisplayDialog(false);
        setWhatsappStep(1);
        setWhatsappId(null);
        checkWhatsAppConnection();
      } else {
        toast.current?.show({
          severity: "error",
          summary: localeText("error"),
          detail: response.message || localeText("invalidConfirmationCode"),
          life: 5000,
        });
      }
    } catch (error) {
      console.error("Erro ao confirmar WhatsApp:", error);
      toast.current?.show({
        severity: "error",
        summary: localeText("error"),
        detail: error.response?.data?.message || localeText("errorConfirmingCode"),
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    if (selectedIntegration?.isWhatsApp) {
      if (whatsappStep === 1) {
        handleWhatsAppAuth();
      } else {
        handleWhatsAppConfirm();
      }
    } else {
      setDisplayDialog(false);
      toast.current?.show({
        severity: "success",
        summary: localeText("success"),
        detail: localeText("configurationSavedSuccessfully"),
        life: 3000,
      });
    }
  };

  const handleSwitch = async (input) => {
    try {
      const isCurrentlyConnected = whatsappConnected?.connected;

      if (isCurrentlyConnected) {
        setIntegrationToDelete("whatsapp");
        setShowDeleteConfirmModal(true);
      } else {
        setSelectedIntegration(integrations.find((i) => i.isWhatsApp));
        setWhatsappStep(1);
        setWhatsappId(null);
        setDisplayDialog(true);
      }
    } catch (error) {
      console.error("Erro ao alternar integração:", error);
    }
  };

  const handleGoogleCalendarSwitch = async () => {
    try {
      const isCurrentlyConnected = googleCalendarConnected?.connected;

      if (isCurrentlyConnected) {
        setIntegrationToDelete("google-calendar");
        setShowDeleteConfirmModal(true);
      } else {
        setLoading(true);
        await handleGoogleCalendarAuth();
        setLoading(false);
      }
    } catch (error) {
      console.error("Erro ao alternar integração Google Calendar:", error);
      setLoading(false);
    }
  };

  const handleDeleteIntegration = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("__extraconomy_token");
      const response = await whatsAppService.deleteConnection(token, whatsappConnected.id);

      if (response.messageCode === 200001) {
        setWhatsappConnected({ connected: false });
        setDisplayDialog(false);

        toast.current?.show({
          severity: "info",
          summary: localeText("whatsappDisconnected"),
          detail: localeText("whatsappHasBeenDisconnected"),
          life: 5000,
        });
      } else {
        toast.current?.show({
          severity: "error",
          summary: localeText("error"),
          detail: response.message || localeText("errorDisconnectingWhatsApp"),
          life: 5000,
        });
      }
    } catch (error) {
      console.error("Erro ao desconectar WhatsApp:", error);

      toast.current?.show({
        severity: "error",
        summary: localeText("error"),
        detail: error.response?.data?.message || localeText("errorDisconnectingWhatsApp"),
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGoogleCalendar = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("__extraconomy_token");
      const userId = userInfo?.id;

      if (!userId) {
        toast.current?.show({
          severity: "error",
          summary: localeText("error"),
          detail: localeText("errorDisconnectingGoogleCalendar"),
          life: 5000,
        });
        return;
      }

      const response = await googleCalendarService.deleteConnection(token, userId);

      if (response.message === "Google Calendar desconectado com sucesso") {
        setGoogleCalendarConnected({ connected: false });

        toast.current?.show({
          severity: "info",
          summary: localeText("googleCalendarDisconnected"),
          detail: localeText("googleCalendarHasBeenDisconnected"),
          life: 5000,
        });

        checkGoogleCalendarConnection();
      } else {
        toast.current?.show({
          severity: "error",
          summary: localeText("error"),
          detail: response.message || localeText("errorDisconnectingGoogleCalendar"),
          life: 5000,
        });
      }
    } catch (error) {
      console.error("Erro ao desconectar Google Calendar:", error);

      toast.current?.show({
        severity: "error",
        summary: localeText("error"),
        detail: error.response?.data?.message || localeText("errorDisconnectingGoogleCalendar"),
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteIntegration = async () => {
    try {
      setLoading(true);
      setShowDeleteConfirmModal(false);

      if (integrationToDelete === "whatsapp") {
        await handleDeleteIntegration();
      } else if (integrationToDelete === "google-calendar") {
        await handleDeleteGoogleCalendar();
      }

      setIntegrationToDelete(null);
    } catch (error) {
      console.error("Erro ao confirmar exclusão:", error);
    } finally {
      setLoading(false);
    }
  };

  const cancelDeleteIntegration = () => {
    setShowDeleteConfirmModal(false);
    setIntegrationToDelete(null);
  };

  const getDeleteModalContent = () => {
    if (integrationToDelete === "whatsapp") {
      return {
        title: localeText("removeWhatsAppIntegration"),
        description: localeText("removeWhatsAppIntegrationDescription"),
      };
    } else if (integrationToDelete === "google-calendar") {
      return {
        title: localeText("removeGoogleCalendarIntegration"),
        description: localeText("removeGoogleCalendarIntegrationDescription"),
      };
    }
    return { title: "", description: "" };
  };

  return (
    <div>
      <Toast ref={toast} />

      {!customerIntegrationsPage && (
        <>
          <p className="m-0 text-lg font-semibold" style={{ color: "var(--theme-text)" }}>
            {localeText("integrations")}
          </p>
          <p className="text-sm">{localeText("integrationsDescription")}</p>
        </>
      )}

      <div className="grid mt-4">
        {integrations.map((integration) => (
          <div key={integration.id} className="col-12 md:col-6 lg:col-4">
            <div
              className="border-1 border-300 border-round-lg p-3 h-full flex flex-column"
              style={{ backgroundColor: "var(--surface-card)" }}
            >
              <div className="flex justify-content-between align-content-start">
                <div
                  className="border-1 border-300 overflow-hidden mb-3 flex align-items-center justify-content-center"
                  style={{
                    width: "fit-content",
                    backgroundColor: "var(--surface-card)",
                    padding: "4px",
                    borderRadius: "100%",
                  }}
                >
                  <img
                    src={integration.url}
                    alt={integration.name}
                    style={{ width: "32px", height: "32px" }}
                  />
                </div>

                {integration.isWhatsApp && (
                  <InputSwitch
                    id="inputSwitchDefault"
                    checked={whatsappConnected?.connected}
                    onChange={handleSwitch}
                    disabled={loading || checkingConnection}
                  />
                )}
                {integration.id === "google-calendar" && (
                  <InputSwitch
                    id="inputSwitchDefault"
                    checked={googleCalendarConnected?.connected}
                    onChange={handleGoogleCalendarSwitch}
                    disabled={loading}
                  />
                )}
              </div>
              <div className="flex align-items-center justify-content-start gap-2 mb-2">
                <h4
                  className="mt-0 mb-0 text-lg font-semibold"
                  style={{ color: "var(--theme-text)" }}
                >
                  {integration.name}
                </h4>

                {integration.isWhatsApp && !checkingConnection && (
                  <span
                    className="text-xs px-2 py-1 border-round"
                    style={{
                      backgroundColor: whatsappConnected?.connected
                        ? "var(--green-100)"
                        : "#FFECC0",
                      color: whatsappConnected?.connected ? "var(--green-700)" : "#624C18",
                    }}
                  >
                    {whatsappConnected?.connected
                      ? localeText("activated")
                      : localeText("deactivated")}
                  </span>
                )}

                {integration.id === "google-calendar" && !checkingConnection && (
                  <span
                    className="text-xs px-2 py-1 border-round"
                    style={{
                      backgroundColor: googleCalendarConnected?.connected
                        ? "var(--green-100)"
                        : "#FFECC0",
                      color: googleCalendarConnected?.connected ? "var(--green-700)" : "#624C18",
                    }}
                  >
                    {googleCalendarConnected?.connected
                      ? localeText("activated")
                      : localeText("deactivated")}
                  </span>
                )}
              </div>

              <p className="text-sm text-color-secondary mb-3 flex-grow-1">
                {integration.description}
              </p>

              <Button
                className="p-button-outlined w-full flex align-items-center justify-content-center gap-2"
                style={{
                  backgroundColor: "var(--primary-alpha-100)",
                  border: "none",
                  color: "var(--primary-alpha-500)",
                }}
                onClick={() => handleConfigure(integration)}
              >
                <i
                  className={
                    integration.id === "google-calendar" && googleCalendarConnected?.connected
                      ? "pi pi-calendar"
                      : "pi pi-cog"
                  }
                ></i>
                <span>
                  {integration.id === "google-calendar" && googleCalendarConnected?.connected
                    ? localeText("viewSchedules")
                    : localeText("configure")}
                </span>
              </Button>
            </div>
          </div>
        ))}
      </div>

      <IntegrationSidebar
        visible={displayDialog}
        onHide={() => {
          setDisplayDialog(false);
          setWhatsappStep(1);
          setWhatsappId(null);
        }}
        selectedIntegration={selectedIntegration}
        configs={configs}
        setConfigs={setConfigs}
        whatsappStep={whatsappStep}
        setWhatsappStep={setWhatsappStep}
        loading={loading}
        onSave={handleSave}
        onDelete={handleDeleteIntegration}
        onDeleteClick={() => {
          setIntegrationToDelete("whatsapp");
          setShowDeleteConfirmModal(true);
        }}
        isConnected={whatsappConnected?.connected}
        whatsappNumber={whatsappConnected?.whatsappNumber}
      />

      <ConfirmationModal
        visible={showDeleteConfirmModal}
        onHide={cancelDeleteIntegration}
        onConfirm={confirmDeleteIntegration}
        getModalContent={getDeleteModalContent}
        localeText={localeText}
        loading={loading}
        type={TYPES.DANGER}
      />
    </div>
  );
};

export default StoreSettingsIntegrationsTab;
